/**
 * Unfulfilled Roles Table Component
 * Displays roles from Resource Budget that have unfulfilled hours
 * (where allocated budget hours exceed currently assigned hours)
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import type { RoleBudget } from '@/types/budget.types';
import type { Employee, ResourceAllocation } from '@/types';
import type { AssignmentSelection } from '@/types/assignment.types';
import { AssignmentModalEnhanced } from '@/components/assignment-modal-enhanced';

interface UnfulfilledRole {
  role: string;
  hoursNeeded: number;
  budgetHours: number;
  allocatedHours: number;
}

interface UnfulfilledRolesTableProps {
  roles: RoleBudget[];
  projectId: number;
  projectStartDate: string;
  projectEndDate: string;
  availablePeople: Employee[];
  existingAllocations: ResourceAllocation[];
  onAssign?: (role: string, selections: AssignmentSelection[]) => void;
}

/**
 * Calculate unfulfilled roles from resource budget data
 */
function calculateUnfulfilledRoles(roles: RoleBudget[]): UnfulfilledRole[] {
  return roles
    .filter((role) => role.budgetHours > role.allocatedHours)
    .map((role) => ({
      role: role.role,
      hoursNeeded: role.budgetHours - role.allocatedHours,
      budgetHours: role.budgetHours,
      allocatedHours: role.allocatedHours,
    }))
    .sort((a, b) => b.hoursNeeded - a.hoursNeeded); // Sort by hours needed (descending)
}

/**
 * Format hours for display
 */
function formatHours(hours: number): string {
  return hours.toFixed(1);
}

export function UnfulfilledRolesTable({ 
  roles, 
  projectId,
  projectStartDate,
  projectEndDate,
  availablePeople,
  existingAllocations,
  onAssign 
}: UnfulfilledRolesTableProps) {
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UnfulfilledRole | null>(null);
  
  const unfulfilledRoles = calculateUnfulfilledRoles(roles);

  // Don't render if there are no unfulfilled roles
  if (unfulfilledRoles.length === 0) {
    return null;
  }

  const handleAssignClick = (role: UnfulfilledRole) => {
    setSelectedRole(role);
    setAssignmentModalOpen(true);
  };

  const handleAssignmentConfirm = (selections: AssignmentSelection[]) => {
    if (selectedRole && onAssign) {
      onAssign(selectedRole.role, selections);
    } else if (selectedRole) {
      // Default behavior if no onAssign callback provided
      toast.success(
        `Assigned ${selections.length} team member${selections.length !== 1 ? 's' : ''} to ${selectedRole.role} role`,
        {
          description: selections.map(s => s.personName).join(', '),
        }
      );
    }
    setAssignmentModalOpen(false);
    setSelectedRole(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Unfulfilled Roles</CardTitle>
        <CardDescription>
          Roles that need additional team members assigned based on Resource Budget allocations
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Hours Needed</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {unfulfilledRoles.map((unfulfilledRole) => (
              <TableRow key={unfulfilledRole.role}>
                <TableCell className="font-medium">{unfulfilledRole.role}</TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-col items-end">
                    <span className="font-semibold text-amber-600">
                      {formatHours(unfulfilledRole.hoursNeeded)}h
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatHours(unfulfilledRole.allocatedHours)}h / {formatHours(unfulfilledRole.budgetHours)}h
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAssignClick(unfulfilledRole)}
                    className="gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    Assign
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {/* Assignment Modal */}
      {selectedRole && (
        <AssignmentModalEnhanced
          open={assignmentModalOpen}
          onOpenChange={setAssignmentModalOpen}
          roleName={selectedRole.role}
          requiredHours={selectedRole.hoursNeeded}
          requiredSkills={[]} // Could be enhanced to extract from role budget
          projectId={projectId}
          projectStartDate={projectStartDate}
          projectEndDate={projectEndDate}
          availablePeople={availablePeople}
          existingAllocations={existingAllocations}
          onConfirm={handleAssignmentConfirm}
          allowMultiple={true}
        />
      )}
    </Card>
  );
}
